import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/hub';

export type FooterConfig = {
  title: string;
  items: {
    title: string;
    href: string;
    skipTracking?: boolean;
  }[];
}[];

export const useFooterConfig = (): FooterConfig => {
  const isLoggedIn = useLoginState() === 'LOGGED_IN';
  const { locale, formatMessage } = useIntl();

  return useMemo(() => {
    const footerConfig: FooterConfig = [
      {
        title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_COMPANY' }),
        items: [
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_NEWWORKSE',
            }),
            href:
              locale === 'de'
                ? 'https://www.new-work.se/de'
                : 'https://www.new-work.se/en',
          },
          {
            title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_CAREER' }),
            href:
              locale === 'de'
                ? 'https://www.new-work.se/de/karriere'
                : 'https://www.new-work.se/en/career/',
          },
          {
            title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_PRESS' }),
            href:
              locale === 'de'
                ? 'https://corporate.xing.com/de/Newsroom/pressekontakt/pressemitteilungen'
                : 'https://corporate.xing.com/en/press-service/press-releases',
          },
        ],
      },
      {
        title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_HELP' }),
        items: [
          {
            title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_HELP' }),
            href:
              locale === 'de'
                ? 'https://help.xing.com/hc/de?segment=b2c'
                : 'https://help.xing.com/hc/en-us?segment=b2c',
          },
        ],
      },
      {
        title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_MEMBERSHIPS' }),
        items: [
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_PREMIUM_MEMBERSHIP',
            }),
            href: 'https://www.xing.com/purchase/premium?reagent=uplt_98',
          },
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_PROJOBS_MEMBERSHIP',
            }),
            href: 'https://www.xing.com/purchase/projobs?reagent=uplt_205',
          },
        ],
      },
    ];

    if (isLoggedIn) {
      footerConfig.push({
        title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_APPS' }),
        items: [
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_IPHONE_IPAD',
            }),
            href: 'https://itunes.apple.com/de/app/xing/id297625850?mt=8&ct=footer_Marketing&at=10lRnD',
            skipTracking: true,
          },
          {
            title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_ANDROID' }),
            href: 'https://play.google.com/store/apps/details?id=com.xing.android&referrer=utm_source%3DXINGWeb%26utm_medium%3DFooter',
            skipTracking: true,
          },
        ],
      });
    }

    if (!isLoggedIn) {
      footerConfig.push({
        title: formatMessage({ id: 'VERTICAL_NAVIGATION_FOOTER_YOUR_XING' }),
        items: [
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_PEOPLE_DIRECTORY',
            }),
            href: 'https://www.xing.com/people/',
          },
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_JOBS_DIRECTORY',
            }),
            href: 'https://www.xing.com/jobs/directory/a',
          },
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_COMPANIES',
            }),
            href: 'https://www.xing.com/companies',
          },
          ...(locale === 'de'
            ? [
                {
                  title: formatMessage({
                    id: 'VERTICAL_NAVIGATION_FOOTER_JOB_DIRECTORY',
                  }),
                  href: 'https://berufe.xing.com/',
                },
              ]
            : []),
        ],
      });
      footerConfig.push({
        title: 'Services',
        items: [
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_TALENT_MANAGER',
            }),
            href:
              locale === 'de'
                ? 'https://recruiting.xing.com/de/'
                : 'https://recruiting.xing.com/en/',
          },
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_ADVERTISE',
            }),
            href:
              locale === 'de'
                ? 'https://werben.xing.com/de'
                : 'https://werben.xing.com/en',
          },
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_LEBENSLAUF',
            }),
            href: 'https://lebenslauf.com/',
          },
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_ANSCHREIBEN',
            }),
            href: 'https://anschreiben.com/',
          },
          {
            title: formatMessage({
              id: 'VERTICAL_NAVIGATION_FOOTER_BEWERBUNG',
            }),
            href: 'https://bewerbung.com/',
          },
        ],
      });
    }

    return footerConfig;
  }, [isLoggedIn, locale, formatMessage]);
};
